import { FC, useEffect, useState } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import { useWalletModal } from '@solana/wallet-adapter-react-ui'
import type { PaymentInfo } from '@/types/invoice'
import { usePayment } from '@/hooks/usePayment'
import { useIsMobile } from '@/hooks/useIsMobile'
import { MobileWalletPicker } from './MobileWalletPicker'
import { formatCurrency, truncateAddress } from '@/utils/format'
import { getPdfUrl } from '@/services/api'

interface PaymentSectionProps {
  paymentToken: string
  payment: PaymentInfo
  invoiceNumber: string
  total: string
  currency: string
  onSuccess: () => void
}

export const PaymentSection: FC<PaymentSectionProps> = ({
  paymentToken,
  payment,
  invoiceNumber,
  total,
  currency,
  onSuccess,
}) => {
  const { connected, publicKey, disconnect } = useWallet()
  const { setVisible } = useWalletModal()
  const { status, error, result, pay, reset } = usePayment()
  const { isMobile, isInWalletBrowser } = useIsMobile()
  const [showWalletPicker, setShowWalletPicker] = useState(false)
  
  // Show mobile wallet picker only on mobile AND not already in a wallet's browser
  const showMobileWalletOption = isMobile && !isInWalletBrowser

  const handlePay = async () => {
    await pay(paymentToken, payment.paymentToken)
  }

  // Trigger callback when payment succeeds
  useEffect(() => {
    if (status === 'success') {
      onSuccess()
    }
  }, [status, onSuccess])

  const isProcessing = ['preparing', 'signing', 'submitting'].includes(status)

  // Success state - full screen takeover style
  if (status === 'success' && result) {
    return (
      <div className="py-8 text-center">
        {/* Success icon */}
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#dcfce7] border-2 border-[#16a34a] flex items-center justify-center">
          <svg className="w-10 h-10 text-[#16a34a]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        
        {/* Title */}
        <h3 className="text-2xl font-semibold text-[#16a34a] mb-2">Paid!</h3>
        
        {/* Amount */}
        <p className="text-lg text-[#111827] mb-1">
          {formatCurrency(total, currency)}
        </p>
        <p className="text-sm text-[#6b7280] mb-6">
          Invoice {invoiceNumber}
        </p>
        
        {/* Transaction link */}
        <a
          href={result.explorerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-[#6b7280] hover:text-[#111827]"
        >
          <span className="font-mono">{truncateAddress(result.signature, 8)}</span>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>

        <div className="mt-6">
          <a
            href={getPdfUrl(paymentToken)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-[#166534] bg-[#dcfce7] rounded-full hover:bg-[#bbf7d0] transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download Receipt
          </a>
        </div>
      </div>
    )
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="py-8 text-center">
        {/* Error icon */}
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#fee2e2] border-2 border-[#dc2626] flex items-center justify-center">
          <svg className="w-10 h-10 text-[#dc2626]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        
        {/* Title */}
        <h3 className="text-2xl font-semibold text-[#dc2626] mb-2">Failed</h3>
        
        {/* Error message */}
        <p className="text-sm text-[#6b7280] mb-6 max-w-xs mx-auto">
          {error || 'Your payment could not be completed'}
        </p>
        
        {/* Retry button */}
        <button
          onClick={reset}
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#111827] text-white text-sm font-medium rounded-full hover:bg-[#1f2937] transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Try again
        </button>
      </div>
    )
  }

  // Processing state
  if (isProcessing) {
    return (
      <div className="py-8 text-center">
        {/* Spinning loader */}
        <div className="w-20 h-20 mx-auto mb-6 relative">
          {/* Outer ring */}
          <div className="absolute inset-0 rounded-full border-3 border-[#e5e7eb]" />
          {/* Spinning arc */}
          <div className="absolute inset-0 rounded-full border-3 border-transparent border-t-[#111827] animate-spin" />
          {/* Center icon */}
          <div className="absolute inset-3 rounded-full bg-white flex items-center justify-center">
            <svg className="w-8 h-8 text-[#111827]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
            </svg>
          </div>
        </div>
        
        {/* Status text */}
        <h3 className="text-2xl font-semibold text-[#111827] mb-2">
          {status === 'preparing' && 'Preparing...'}
          {status === 'signing' && 'Confirm in wallet'}
          {status === 'submitting' && 'Processing...'}
        </h3>
        
        {/* Amount */}
        <p className="text-lg text-[#6b7280] mb-6">
          {formatCurrency(total, currency)}
        </p>
        
        {/* Confirming indicator */}
        <div className="flex items-center justify-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#111827] animate-pulse" />
          <span className="text-sm text-[#6b7280]">
            {status === 'signing' ? 'Waiting for signature' : 'Confirming on Solana'}
          </span>
        </div>
      </div>
    )
  }

  // Default state - show payment form
  return (
    <div className="space-y-6">
      {/* Amount display - prominent */}
      <div className="text-center py-4">
        <p className="text-xs text-[#6b7280] uppercase tracking-wide mb-1">Amount due</p>
        <p className="text-3xl font-semibold text-[#111827]">
          {parseFloat(payment.totalInUsdc).toLocaleString()} {payment.paymentToken}
        </p>
        <p className="text-sm text-[#6b7280] mt-1">
          {formatCurrency(total, currency)}
        </p>
      </div>

      {/* Connected wallet info */}
      {connected && publicKey && (
        <div className="flex items-center justify-between py-3 px-4 bg-[#f9fafb] rounded-lg">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#16a34a]" />
            <span className="text-sm text-[#374151] font-mono">
              {truncateAddress(publicKey.toBase58(), 6)}
            </span>
          </div>
          <button
            onClick={() => disconnect()}
            className="text-xs text-[#6b7280] hover:text-[#111827]"
          >
            Disconnect
          </button>
        </div>
      )}

      {/* Pay button or connect button */}
      {!connected ? (
        <div className="space-y-3">
          {/* Mobile: Show "Open in wallet" button (only if not already in wallet browser) */}
          {showMobileWalletOption && (
            <button
              onClick={() => setShowWalletPicker(true)}
              className="w-full h-14 bg-[#111827] text-white text-base font-semibold rounded-full hover:bg-[#1f2937] transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Open in wallet app
            </button>
          )}
          
          {/* Browser wallet connect - always show on desktop, show as secondary on mobile */}
          <button
            onClick={() => setVisible(true)}
            className={
              showMobileWalletOption
                ? "w-full h-12 text-[#6b7280] text-sm font-medium hover:text-[#111827] transition-colors"
                : "w-full h-14 bg-[#111827] text-white text-base font-semibold rounded-full hover:bg-[#1f2937] transition-colors"
            }
          >
            {showMobileWalletOption ? 'Or connect browser wallet' : 'Connect wallet to pay'}
          </button>
        </div>
      ) : (
        <button
          onClick={handlePay}
          className="w-full h-14 bg-[#111827] text-white text-base font-semibold rounded-full hover:bg-[#1f2937] transition-colors"
        >
          Pay {parseFloat(payment.totalInUsdc).toLocaleString()} {payment.paymentToken}
        </button>
      )}

      {/* Mobile Wallet Picker Modal */}
      <MobileWalletPicker
        isOpen={showWalletPicker}
        onClose={() => setShowWalletPicker(false)}
      />

      {/* Footer links */}
      <div className="flex items-center justify-between text-xs text-[#9ca3af]">
        <span>Fees covered by soljar</span>
        <a
          href={getPdfUrl(paymentToken)}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-[#6b7280] flex items-center gap-1"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download PDF
        </a>
      </div>
    </div>
  )
}
